import React, { type FC, useEffect } from 'react';
import { Modal } from '@wix/design-system';
import { DataHooks } from '../../../constants/dataHooks';
import { CLIModalLayout } from './CLIModal/CLIModalLayout';
import { BlocksModal } from './BlocksModal/BlocksModal';
import { SelfHostedModal } from './SelfHostedModal/SelfHostedModal';
import {
  isBlocksTemplateRequiresNamespace,
  isSelfHostedTemplate,
} from '../../../selectors/templateType-selectors';
import { QueryParams } from '../../../constants/QueryParams';
import { useReturnedFromLogin } from '../../../hooks/useReturnedFromLogin';
import { useQueryParams } from '../../../hooks/useQueryParams';
import { dispatchDisableSearchShortcutEvent } from '../../../utils/dispatchDisableSearchShortcutEvent';
import { useTemplatesData } from '../../../hooks/useTemplatesData';
import { ActiveModal } from '../../../types/ActiveModal';

const ModalsConfiguration = {
  [ActiveModal.CLI]: {
    dataHook: DataHooks.CLIModal,
    ModalLayout: CLIModalLayout,
  },
  [ActiveModal.SELF_HOSTED]: {
    dataHook: DataHooks.SelfHostedModal,
    ModalLayout: SelfHostedModal,
  },
  [ActiveModal.BLOCKS]: {
    dataHook: DataHooks.BlocksModal,
    ModalLayout: BlocksModal,
  },
  [ActiveModal.NONE]: {
    dataHook: '',
    ModalLayout: () => <></>,
  },
};

interface ModalRendererProps {
  onClose?: () => void;
}

export const ModalRenderer: FC<ModalRendererProps> = ({ onClose }) => {
  const { currentTemplate: template } = useTemplatesData();
  const { activeModal, setActiveModal } = useTemplatesData();
  const { ModalLayout, dataHook } = ModalsConfiguration[activeModal];
  const { removeParam } = useQueryParams();
  const closeModal = () => {
    setActiveModal(ActiveModal.NONE);
    onClose?.();
  };
  const { returnedFromLogin } = useReturnedFromLogin();
  const isActiveModal = activeModal !== ActiveModal.NONE;

  useEffect(() => {
    if (returnedFromLogin) {
      if (isBlocksTemplateRequiresNamespace(template)) {
        setActiveModal(ActiveModal.BLOCKS);
      } else if (isSelfHostedTemplate(template)) {
        setActiveModal(ActiveModal.SELF_HOSTED);
      }
    }
    removeParam(QueryParams.RETURNED_FROM_LOGIN_PAGE);
    removeParam(QueryParams.REF);
  }, [returnedFromLogin]);

  useEffect(
    () => dispatchDisableSearchShortcutEvent(isActiveModal),
    [isActiveModal],
  );

  return (
    <Modal dataHook={dataHook} isOpen={isActiveModal}>
      <ModalLayout closeModal={closeModal} />
    </Modal>
  );
};
